import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  DollarSign,
  MousePointerClick,
  Eye,
  Users,
  Building2,
  ChevronDown,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Clock,
  Wifi,
  WifiOff,
  Activity,
  TrendingUp,
  TrendingDown,
  Search,
  Download,
  Settings,
  Bell,
  Filter,
  MoreHorizontal,
  Play,
  Pause,
  ExternalLink,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
  XCircle,
} from "lucide-react";
import axios from "axios";
import styles from "./CampaignsDashboard.module.css";

// === HELPERS ===
const formatNum = (n) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return n?.toString() || "0";
};

const formatMoney = (n) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n || 0);
};

const formatPct = (n) => `${Number(n || 0).toFixed(1)}%`;

const formatTime = (date) => {
  if (!date) return "Never";
  const d = new Date(date);
  const now = new Date();
  const diff = now - d;

  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 2592000000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
};

export default function LiveCampaignsDashboard() {
  const [campaigns, setCampaigns] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({
    totalLeads: 0,
    totalPages: 1,
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [leadsLoading, setLeadsLoading] = useState(false);

  const [adAccount, setAdAccount] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("last_updated");
  const [sortOrder, setSortOrder] = useState("desc");
  const [error, setError] = useState("");
  const [isConnecting, setIsConnecting] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    totalLeads: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 10,
  });

  const userId = localStorage.getItem("userId");
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000;
  const token = localStorage.getItem("token");

  // ==================== FETCH LEADS FOR SELECTED CAMPAIGN ====================
  const fetchLeads = useCallback(async (mongoCampaignId, page = 1) => {
    if (!mongoCampaignId || !token) return;

    setLeadsLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `https://prod.toxboxai.com/api/campaign/campaign/${mongoCampaignId}/leads`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setLeads(res.data.data || []);
        setPagination({
          totalLeads: res.data.pagination.totalLeads || 0,
          totalPages: res.data.pagination.totalPages || 1,
          currentPage: res.data.pagination.currentPage || 1,
          hasNextPage: res.data.pagination.hasNextPage || false,
          hasPreviousPage: res.data.pagination.hasPreviousPage || false,
        });
      } else {
        throw new Error("API returned success: false");
      }
    } catch (err) {
      console.error("Failed to fetch leads:", err);
      setError("Failed to load leads for this campaign");
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  }, [token]);

  // ==================== HANDLE CAMPAIGN CLICK ====================
  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign);
    const mongoId = campaign._id;
    if (mongoId) {
      fetchLeads(mongoId, 1);
    } else {
      setError("Campaign ID missing");
    }
  };

  // ==================== PAGINATION ====================
  const goToPage = (page) => {
    if (selectedCampaign?._id) {
      fetchLeads(selectedCampaign._id, page);
    }
  };

  // ==================== WEBSOCKET CONNECTION ====================
  const connectWebSocket = useCallback(() => {
    if (!userId) {
      console.log("No userId found, cannot connect WebSocket");
      setIsConnecting(false);
      return;
    }

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
    }
    // or wherever you store it

    if (!token) {
      setError("No authentication token found");
      setIsConnecting(false);
      return;
    }
    console.log(`Attempting WebSocket connection (attempt ${connectionAttempts + 1})`);
    setIsConnecting(true);

    const ws = new WebSocket(`wss://prod.toxboxai.com/ws/meta?userId=${userId}&token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("Meta WebSocket connected successfully");
      setIsConnected(true);
      setIsConnecting(false);
      setError("");
      setConnectionAttempts(0);
      addNotification("Connected to live data", "success");
    };

    ws.onmessage = (event) => {
      try {
        const { type, data, timestamp } = JSON.parse(event.data);
        setLastUpdate(new Date(timestamp || Date.now()));

        switch (type) {
          case "init":
            setCampaigns(
              Object.fromEntries(data.campaigns.map((c) => [c.campaign_id, c]))
            );
            setLeads(data.leads || []);
            addNotification(
              `Loaded ${data.campaigns?.length || 0} campaigns`,
              "info"
            );
            break;

          case "new_lead":
            setLeads((prev) => {
              const exists = prev.some((l) => l.lead_id === data.lead_id);
              if (exists) return prev;
              // Keep ALL leads – no slicing
              return [data, ...prev];
            });

            if (data.campaign_id) {
              setCampaigns((prev) => ({
                ...prev,
                [data.campaign_id]: {
                  ...prev[data.campaign_id],
                  leads_count: (prev[data.campaign_id]?.leads_count || 0) + 1,
                },
              }));
            }
            addNotification(
              `New lead: ${data.name || data.email || "Unknown"} from ${
                data.platform
              }`,
              "success"
            );
            break;

          case "campaign_update":
            setCampaigns((prev) => ({
              ...prev,
              [data.id]: {
                ...prev[data.id],
                ...data,
                campaign_id: data.id,
              },
            }));
            break;

          case "ping":
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(
                JSON.stringify({
                  type: "pong",
                  timestamp: new Date().toISOString(),
                })
              );
            }
            break;
        }
      } catch (err) {
        console.error("[WS_PARSE] Error parsing WebSocket message:", err);
      }
    };

    ws.onerror = (error) => {
      console.error("[WS_ERROR] WebSocket error:", error);
      setIsConnected(false);
    };

    ws.onclose = (event) => {
      console.log(`[WS_CLOSE] WebSocket closed: ${event.code}`);
      setIsConnected(false);
      setIsConnecting(false);

      if (connectionAttempts < maxReconnectAttempts) {
        const delay = Math.min(
          baseReconnectDelay * Math.pow(2, connectionAttempts),
          30000
        );
        setError(`Reconnecting in ${Math.ceil(delay / 1000)}s...`);
        setConnectionAttempts((prev) => prev + 1);

        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, delay);
      } else {
        setError("Connection lost. Please refresh the page.");
      }
    };
  }, [userId, connectionAttempts]);

  // === NOTIFICATION SYSTEM ===
  const addNotification = (message, type = "info") => {
    const id = Date.now();
    const notification = { id, message, type, timestamp: new Date() };

    setNotifications((prev) => [notification, ...prev].slice(0, 5));

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  // === INITIALIZATION ===
  useEffect(() => {
    if (!userId) {
      setIsConnecting(false);
      setError("Please log in to view live data");
      return;
    }

    loadInitialData();
    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current)
        clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
      }
    };
  }, [userId, connectWebSocket]);

  const loadInitialData = async (page = 1) => {
    try {
      const response = await axios.get(`https://prod.toxboxai.com/api/meta/${userId}/live-data`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.success) {
        const {
          campaigns: campaignList,
          leads: leadsList,
          pagination,
        } = response.data.data;
        setCampaigns(
          Object.fromEntries(campaignList.map((c) => [c.campaign_id, c]))
        );
        // Load ALL leads – no limit
        setLeads(leadsList);
        setPaginationInfo(pagination); // ⬅ NEW
      }
    } catch (error) {
      console.error("Failed to load initial data:", error.message);
    }
  };

  const syncLeads = async () => {
    if (!userId) return;
    setError("");

    try {
      const response = await axios.post(
        `https://prod.toxboxai.com/api/meta/${userId}/sync-leads?days=30`
      );
      if (response.data.success) {
        addNotification(`Synced ${response.data.synced} new leads`, "success");
        await loadInitialData();
      }
    } catch (err) {
      setError("Sync failed: " + err.message);
      addNotification("Sync failed", "error");
    }
  };

  const forceReconnect = () => {
    setConnectionAttempts(0);
    setError("");
    connectWebSocket();
  };

  // === DERIVED DATA ===
  const adAccounts = {};
  Object.values(campaigns).forEach((c) => {
    if (!adAccounts[c.ad_account_id]) {
      adAccounts[c.ad_account_id] = c.ad_account_name || c.ad_account_id;
    }
  });

  const filteredCampaigns = Object.values(campaigns)
    .filter((c) => {
      const matchesAccount = !adAccount || c.ad_account_id === adAccount;
      const matchesCampaign = !campaignId || c.campaign_id === campaignId;
      const matchesSearch =
        !searchTerm ||
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.ad_account_name?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesAccount && matchesCampaign && matchesSearch;
    })
    .sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "spend":
          aVal = a.spend || 0;
          bVal = b.spend || 0;
          break;
        case "leads_count":
          aVal = a.leads_count || 0;
          bVal = b.leads_count || 0;
          break;
        case "ctr":
          aVal = a.ctr || 0;
          bVal = b.ctr || 0;
          break;
        default:
          aVal = new Date(a.last_updated || 0);
          bVal = new Date(b.last_updated || 0);
      }

      if (sortOrder === "desc") {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    });

  const filteredLeads = leads.filter((l) => {
    const matchesAccount =
      !adAccount ||
      (l.campaign_id && campaigns[l.campaign_id]?.ad_account_id === adAccount);
    const matchesCampaign = !campaignId || l.campaign_id === campaignId;
    const matchesSearch =
      !searchTerm ||
      l.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.phone?.includes(searchTerm);

    return matchesAccount && matchesCampaign && matchesSearch;
  });

  // Calculate summary stats
  const totalSpend = filteredCampaigns.reduce(
    (sum, c) => sum + (c.spend || 0),
    0
  );
  const totalLeads = filteredCampaigns.reduce(
    (sum, c) => sum + (c.leads_count || 0),
    0
  );
  const totalImpressions = filteredCampaigns.reduce(
    (sum, c) => sum + (c.impressions || 0),
    0
  );
  const totalClicks = filteredCampaigns.reduce(
    (sum, c) => sum + (c.clicks || 0),
    0
  );
  const avgCTR =
    totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  if (!userId) {
    return (
      <div className={styles.container}>
        <div className={styles.loginPrompt}>
          <AlertCircle size={48} />
          <h2>Authentication Required</h2>
          <p>Please log in to access your Meta campaign dashboard.</p>
          <button className={styles.loginBtn}>Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* HEADER */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Meta Campaign Manager</h1>
          <div className={styles.breadcrumb}>
            <span>Dashboard</span>
            <span>/</span>
            <span>Campaigns</span>
          </div>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.connectionStatus}>
            {isConnected ? (
              <span className={styles.statusOnline}>
                <Wifi size={14} />
                Connected
              </span>
            ) : (
              <span className={styles.statusOffline}>
                <WifiOff size={14} />
                Disconnected
              </span>
            )}
          </div>

          <div className={styles.headerActions}>
            <button onClick={syncLeads} className={styles.syncBtn}>
              <RefreshCw size={16} />
              Sync
            </button>

            <div className={styles.notificationWrapper}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`${styles.iconBtn} ${
                  notifications.length > 0 ? styles.hasNotifications : ""
                }`}
              >
                <Bell size={16} />
                {notifications.length > 0 && (
                  <span className={styles.notificationCount}>
                    {notifications.length}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className={styles.notificationPanel}>
                  <div className={styles.panelHeader}>
                    <h4>Recent Activity</h4>
                  </div>
                  <div className={styles.notificationList}>
                    {notifications.length === 0 ? (
                      <p className={styles.emptyNotifications}>
                        No recent activity
                      </p>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`${styles.notification} ${
                            styles[notif.type]
                          }`}
                        >
                          <p>{notif.message}</p>
                          <span>{formatTime(notif.timestamp)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <button className={styles.iconBtn}>
              <Settings size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ALERTS */}
      {error && (
        <div className={styles.alert}>
          <AlertCircle size={16} />
          <span>{error}</span>
          {!isConnected && (
            <button onClick={forceReconnect} className={styles.retryBtn}>
              Retry
            </button>
          )}
        </div>
      )}

      {isConnecting && (
        <div className={styles.loadingAlert}>
          <div className={styles.spinner}></div>
          <span>Connecting to Meta...</span>
        </div>
      )}

      {/* METRICS CARDS */}
      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <div className={styles.metricHeader}>
            <div className={styles.metricIcon}>
              <DollarSign size={20} />
            </div>
            <span className={styles.metricChange}>
              <TrendingUp size={12} />
              +12.5%
            </span>
          </div>
          <div className={styles.metricValue}>{formatMoney(totalSpend)}</div>
          <div className={styles.metricLabel}>Total Spend</div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricHeader}>
            <div className={styles.metricIcon}>
              <Users size={20} />
            </div>
            <span className={styles.metricChange}>
              <TrendingUp size={12} />
              +8.3%
            </span>
          </div>
          <div className={styles.metricValue}>{totalLeads}</div>
          <div className={styles.metricLabel}>Total Leads</div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricHeader}>
            <div className={styles.metricIcon}>
              <Eye size={20} />
            </div>
            <span className={styles.metricChange}>
              <TrendingUp size={12} />
              +15.7%
            </span>
          </div>
          <div className={styles.metricValue}>
            {formatNum(totalImpressions)}
          </div>
          <div className={styles.metricLabel}>Impressions</div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricHeader}>
            <div className={styles.metricIcon}>
              <MousePointerClick size={20} />
            </div>
            <span
              className={`${styles.metricChange} ${
                avgCTR < 2 ? styles.negative : ""
              }`}
            >
              {avgCTR < 2 ? (
                <TrendingDown size={12} />
              ) : (
                <TrendingUp size={12} />
              )}
              {avgCTR < 2 ? "-2.1%" : "+3.4%"}
            </span>
          </div>
          <div className={styles.metricValue}>{formatPct(avgCTR)}</div>
          <div className={styles.metricLabel}>Avg CTR</div>
        </div>
      </div>

      {/* FILTERS */}
      <div className={styles.filtersPanel}>
        <div className={styles.filtersLeft}>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search campaigns or leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <select
            value={adAccount}
            onChange={(e) => {
              setAdAccount(e.target.value);
              setCampaignId("");
            }}
            className={styles.filterSelect}
          >
            <option value="">All Ad Accounts</option>
            {Object.entries(adAccounts).map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Campaigns</option>
            {filteredCampaigns.map((c) => (
              <option key={c.campaign_id} value={c.campaign_id}>
                {c.name.length > 50 ? c.name.slice(0, 50) + "..." : c.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filtersRight}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="last_updated">Last Updated</option>
            <option value="spend">Spend</option>
            <option value="leads_count">Leads</option>
            <option value="ctr">CTR</option>
            <option value="name">Name</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className={styles.sortBtn}
          >
            {sortOrder === "desc" ? (
              <TrendingDown size={16} />
            ) : (
              <TrendingUp size={16} />
            )}
          </button>

          <button
            onClick={() => {
              setAdAccount("");
              setCampaignId("");
              setSearchTerm("");
            }}
            className={styles.clearBtn}
          >
            Clear
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      {!selectedCampaign ? (
        <>
          <div className={styles.sectionHeader}>
            <h2>Campaigns ({filteredCampaigns.length})</h2>
          </div>

          {filteredCampaigns.length === 0 ? (
            <div className={styles.emptyState}>
              <Activity size={64} />
              <h3>No campaigns found</h3>
              <p>Try adjusting your filters or check your connection.</p>
            </div>
          ) : (
            <div className={styles.cardsGrid}>
              {filteredCampaigns.map((campaign) => (
                <div
                  key={campaign.campaign_id}
                  className={styles.campaignCard}
                  onClick={() => handleCampaignClick(campaign)}
                >
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>
                      {campaign.name.length > 55 ? campaign.name.slice(0, 55) + "..." : campaign.name}
                    </h3>
                    <span className={`${styles.statusBadge} ${styles[campaign.status?.toLowerCase() || "paused"]}`}>
                      {campaign.status === "ACTIVE" ? <Play size={14} /> : <Pause size={14} />}
                      {campaign.status || "PAUSED"}
                    </span>
                  </div>

                  <div className={styles.cardAccount}>
                    <Building2 size={14} /> {campaign.ad_account_name || "Unknown Account"}
                  </div>

                  <div className={styles.cardMetrics}>
                    <div className={styles.metric}>
                      <span className={styles.metricLabel}>Spend</span>
                      <span className={styles.metricValue}>{formatMoney(campaign.spend)}</span>
                    </div>
                    <div className={styles.metric}>
                      <span className={styles.metricLabel}>Leads</span>
                      <span className={styles.metricLeads}>{campaign.leads_count || 0}</span>
                    </div>
                    <div className={styles.metric}>
                      <span className={styles.metricLabel}>CTR</span>
                      <span>{formatPct(campaign.ctr)}</span>
                    </div>
                  </div>

                  <div className={styles.cardFooter}>
                    <Clock size={12} /> {formatTime(campaign.last_updated)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* ==================== LEADS VIEW ==================== */
        <div className={styles.tableSection}>
          <div className={styles.sectionHeader}>
            <button onClick={() => setSelectedCampaign(null)} className={styles.backBtn}>
              <ArrowLeft size={22} /> Back to Campaigns
            </button>
            <h2>
              Leads → {selectedCampaign.name} ({pagination.totalLeads} total)
            </h2>
            <button className={styles.exportBtn}>
              <Download size={18} /> Export CSV
            </button>
          </div>

          {filteredLeads.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Users size={48} />
              </div>
              <h3>No leads yet</h3>
              <p>Leads will appear here when they come from Meta</p>
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.leadsTable}>
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Campaign</th>
                    <th>Source</th>
                    <th>Platform</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Show ALL filtered leads */}
                  {filteredLeads.map(lead => (
                    <tr key={lead.lead_id || lead._id}>
                      <td>
                        <div className={styles.contactCell}>
                          <div className={styles.contactName}>
                            {lead.name || 'No Name'}
                          </div>
                          <div className={styles.contactDetails}>
                            {lead.email && (
                              <span className={styles.contactInfo}>
                                <Mail size={12} />
                                {lead.email}
                              </span>
                            )}
                            {lead.phone && (
                              <span className={styles.contactInfo}>
                                <Phone size={12} />
                                {lead.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.campaignInfo}>
                          {lead.campaign_id && campaigns[lead.campaign_id]
                            ? campaigns[lead.campaign_id].name
                            : <span className={styles.noCampaignBadge}>No campaign</span>
                          }
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.sourceBadge} ${styles[lead.source] || ''}`}>
                          {lead.source === 'realtime_webhook' && 'Live'}
                          {lead.source === 'api_sync_live' && 'Sync'}
                          {lead.source === 'manual_sync' && 'Manual'}
                          {!['realtime_webhook', 'api_sync_live', 'manual_sync'].includes(lead.source) && 'Other'}
                        </span>
                      </td>
                      <td>
                        <span className={styles.platformBadge}>
                          {lead.platform}
                        </span>
                      </td>
                      <td className={styles.timeCell}>{formatTime(lead.created_time)}</td>
                      <td>
                        <div className={styles.leadActions}>
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className={styles.actionBtn}>
                              <Mail size={14} />
                            </a>
                          )}
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className={styles.actionBtn}>
                              <Phone size={14} />
                            </a>
                          )}
                          <button className={styles.actionBtn}>
                            <MoreHorizontal size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}



      <div className={styles.tableWrapper}>
        <table className={styles.leadsTable}>
          <thead>
            <tr>
              <th>Contact</th>
              <th>Campaign</th>
              <th>Source</th>
              <th>Platform</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Show ALL filtered leads */}
            {filteredLeads.map(lead => (
              <tr key={lead.lead_id || lead._id}>
                <td>
                  <div className={styles.contactCell}>
                    <div className={styles.contactName}>
                      {lead.name || 'No Name'}
                    </div>
                    <div className={styles.contactDetails}>
                      {lead.email && (
                        <span className={styles.contactInfo}>
                          <Mail size={12} />
                          {lead.email}
                        </span>
                      )}
                      {lead.phone && (
                        <span className={styles.contactInfo}>
                          <Phone size={12} />
                          {lead.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div className={styles.campaignInfo}>
                    {lead.campaign_id && campaigns[lead.campaign_id]
                      ? campaigns[lead.campaign_id].name
                      : <span className={styles.noCampaignBadge}>No campaign</span>
                    }
                  </div>
                </td>
                <td>
                  <span className={`${styles.sourceBadge} ${styles[lead.source] || ''}`}>
                    {lead.source === 'realtime_webhook' && 'Live'}
                    {lead.source === 'api_sync_live' && 'Sync'}
                    {lead.source === 'manual_sync' && 'Manual'}
                    {!['realtime_webhook', 'api_sync_live', 'manual_sync'].includes(lead.source) && 'Other'}
                  </span>
                </td>
                <td>
                  <span className={styles.platformBadge}>
                    {lead.platform}
                  </span>
                </td>
                <td className={styles.timeCell}>{formatTime(lead.created_time)}</td>
                <td>
                  <div className={styles.leadActions}>
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className={styles.actionBtn}>
                        <Mail size={14} />
                      </a>
                    )}
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className={styles.actionBtn}>
                        <Phone size={14} />
                      </a>
                    )}
                    <button className={styles.actionBtn}>
                      <MoreHorizontal size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


      {/* FOOTER */}
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <Clock size={14} /> Last updated: {formatTime(lastUpdate)}
        </div>
        <div className={styles.footerRight}>
          {isConnected ? (
            <span className={styles.liveIndicator}><CheckCircle size={14} /> Live sync active</span>
          ) : (
            <span className={styles.offlineIndicator}><XCircle size={14} /> Disconnected</span>
          )}
        </div>
      </div>
    </div >

  );
}
